/**
 * Official Vimeo videojs tech plugin.
 *
 * Wraps the official Vimeo Player SDK,
 * @see https://developer.vimeo.com/player/sdk
 */

/* eslint no-dupe-class-members: "off" */
/*
  eslint class-methods-use-this: ["off", {
    "exceptMethods": [
      "controls",
      "supportsFullScreen",
      "createEmbed",
      "isVimeoUrl"
    ]
  }]
*/

import videojs from 'video.js';
import { v4 as uuidv4 } from 'uuid';
import semver from 'semver';
import _ from 'lodash';

import {
  loadScript,
  extractVimeoVideoIdsFromUrl,
} from '../../utilities/helpers/index.js';

import {
  VIMEO_URLS,
  VIMEO_POST_MESSAGE_EVENTS,
} from '../../../snippets/js/constants.cjs';

const [
  Tech,
  registerTech,
  _isOnMobile,
] = [
  videojs.getTech('Tech'),
  videojs.registerTech || videojs.tech,
  videojs.browser.IS_IOS || videojs.browser.IS_NATIVE_ANDROID,
];

let hasUserInteraction = false;

class Vimeo extends Tech {
  constructor(options = {}, ready) {
    super(options, ready);
    if (semver.lt(videojs.VERSION, '7.18.1')) {
      throw new Error(
        `This plugin requires the videojs version 7.18.1. Please upgrade from your current version: ${videojs.VERSION}`,
      );
    }
    this.el_.classList.add('element-wrapper');

    Object.assign(this.el_.style, {
      position: 'relative',
      height: '100%',
    });

    this.uuid = options.uuid || uuidv4();
    this.debug = options.debug;
    this.debugPrefix = options.debugPrefix || this.uuid;
    this.playerStartTime = options.playerStartTime || 0;

    this.log('constructor() passed options', { options });
    this.log('this.playerStartTime: ', this.playerStartTime);

    this.setPoster(this.options_.poster);
    this.setSrc(this.options_.source);
    this.extractVimeoUrlVideoIds();
    this.setTimeout(function initializeVimeoPlayerTimeout() {
      if (this.el_) {
        this.el_.parentNode.className += ' vjs-vimeo';
        if (_isOnMobile)this.el_.parentNode.className += ' vjs-vimeo-mobile';
        if (Vimeo.isScriptReady) this.initializeVimeoPlayer();
        this.triggerReady();
      }
    });
  }

  /**
    * A function that logs a color-coded message to the console if `debug` is `true`.
    *
    * @param {Object} args - A destructred array of all arguments passed into the `log` function which are then logged out in that same order.
    *
    */
  log(...args) {
    /* eslint no-console: "off" */
    if (this.debug) console.log(`%c[${this.debugPrefix}]`, 'color: white; background: #2196F3', ...args);
  }

  /**
   * A function that creates the wrapper element that the Vimeo tech resides within.
   */
  createEl() {
    const div = document.createElement('div');
    div.setAttribute('id', this.options_.techId);
    div.setAttribute('style', 'width:100%;height:100%;top:0;left:0;position:absolute');
    div.setAttribute('class', 'vjs-tech');
    return div;
  }

  /**
    * A function that initializes:
    * - the user interaction listeners,
    * - vimeo player state object,
    * - and the Vimeo player element itself before reporting to the parent VideoJS instance that everything is ready.
    */
  initializeVimeoPlayer() {
    this.log('initializeVimeoPlayer()');
    if (!hasUserInteraction) this.initializeUserInteractionEvents();

    const vimeoOptions = {
      url: this.activeVideoId,
      byline: false,
      portrait: false,
      title: false,
      loop: false,
      controls: false,
      responsive: true,
      autopause: false,
      autoplay: false,
    };

    if (this.options_.autoplay) vimeoOptions.autoplay = true;
    if (this.options_.autopause) vimeoOptions.autopause = this.options_.autopause;
    if (this.options_.height) vimeoOptions.height = this.options_.height;
    if (this.options_.width) vimeoOptions.width = this.options_.width;
    if (this.options_.maxheight) vimeoOptions.maxheight = this.options_.maxheight;
    if (this.options_.maxwidth) vimeoOptions.maxwidth = this.options_.maxwidth;
    if (this.options_.loop) vimeoOptions.loop = this.options_.loop;
    if (this.options_.color) vimeoOptions.color = this.options_.color.replace(/^#/, '');

    this.log('finalized `vimeoOptions`: ', { vimeoOptions });

    if (this.src()?.src) {
      this.vimeoPlayer = new window.Vimeo.Player(this.el().id, vimeoOptions);
      this.hasLoadedVimeoPlayer = true;
      this.log('Initialized the Vimeo player...', this.vimeoPlayer);
    }

    this.setupVimeoPlayerEventBusListeners();
    this.initVimeoState();
    this.setupVimeoPlayerEvents(vimeoOptions);

    this.triggerReady();
    this.log('The Vimeo VideoJS tech has finished initializing.');
  }

  /**
   * Function that extracts the public and private (if it exists) from the Vimeo video URL.
   */
  extractVimeoUrlVideoIds() {
    this.log('extractVimeoUrlVideoIds(): ');
    /**
     * Sent back from the server, is this stream URL structure:
     *
     * `https://vimeo.com/${(!privateSourceVideoId) ? 'video/' : ''}${sourceVideoId}/${(privateSourceVideoId) ? privateSourceVideoId : ''}`,
     *
     * So for public / password Vimeo videos the URL structure would be:
     * - https://vimeo.com/video/<publicId>
     *
     * And for private (unlisted) Vimeo videos the URL structure would be:
     * - https://vimeo.com/<publicId>/<privateHashId>
     */
    this.extractedVimeoVideoIds = extractVimeoVideoIdsFromUrl(
      this.activeVideoId.replace(
        (this.activeVideoId.includes('video') ? 'https://vimeo.com/video/' : 'https://vimeo.com/'), '') // eslint-disable-line
    );
    this.log('extracted vimeo video ids: ', { extractedVimeoVideoIds: this.extractedVimeoVideoIds });
  }

  handleUserInteraction() {
    hasUserInteraction = true;
    this.hasUserInteraction = true;
  }

  /**
  * A function that listens for any click/scroll event.
  * Browser security standards mandate that a page has received user interaction before videos inside iframes can be unmuted.
  */
  initializeUserInteractionEvents() {
    document.removeEventListener('scroll', this.handleUserInteraction);
    document.removeEventListener('click', this.handleUserInteraction);
    document.addEventListener('scroll', this.handleUserInteraction, { once: true });
    document.addEventListener('click', this.handleUserInteraction, { once: true });
  }

  /**
   * Construct the Vimeo player state.
   */
  initVimeoState() {
    this.log('initVimeoState()');
    const state = {
      ended: false,
      playing: false,
      volume: 0,
      passwordProtected: false,
      // NOTE: Adjusting the playback rate is available only to members with a Vimeo Pro+ subscription or higher.
      playbackRate: 1,
      progress: {
        seconds: 0,
        percent: 0,
        duration: 0,
      },
    };
    this._vimeoState = state;
    this.vimeoPlayer.getCurrentTime().then((time) => { state.progress.seconds = time; });
    this.vimeoPlayer.getDuration().then((time) => { state.progress.duration = time; });
    this.vimeoPlayer.getPaused().then((paused) => { state.playing = !paused; });
    this.vimeoPlayer.getVolume().then((volume) => { state.volume = volume; });
    (async () => {
      await this.handleFetchingVimeoCaptions();
    })();
    // TODO TAM clean: Implement a better way of handling user playback rates, since, only
    // Pro accounts and above have access to this.
    this.vimeoPlayer.getPlaybackRate().then((playbackRate) => { state.playbackRate = playbackRate; });
  }

  setupVimeoPlayerEventBusListeners() {
    this.log('`setupVimeoPlayerEventBusListeners()...` and options value', this?.options_);
    if (!this.options_?.eventBus) return;
    const EventBus = this.options_?.eventBus;
    
    EventBus.$on('playposit-vimeo-embed-fetch-captions', async () => {
      await this.handleFetchingVimeoCaptions();
    });
  }

  /**
   * Setup Vimeo player events.
   *
   * @param {object} vimeoOptions Vimeo player options
   */
  setupVimeoPlayerEvents(vimeoOptions) {
    this.log('setupVimeoPlayerEvents()');
    /**
      * Currently, we only show a Vimeo video overlay in the Designer for our older Vimeo video bulbs since in the Player
      * we invoke the fallback stream logic.
      *
      * This Vimeo video overlay would only show ***IF*** the older Vimeo video has restricted Embed or
      * viewing permission set => 'Nowhere' or 'Specific domains' without PP being one of them.
      */
    if (this.options_.service === 'Designer') {
      this.vimeoPlayer.ready().then(() => {
        this.log('Designer specific ready event!');
      }).catch(() => {
        const vimeoDiv = document.createElement('div');
        vimeoDiv.classList.add('vimeo-video-issue-div-container');
        document.body.appendChild(vimeoDiv);
        return this.getOEmbedData({
          videoUrl: this.activeVideoId,
          params: vimeoOptions,
          element: vimeoDiv,
        });
      });
    }

    // Able to actually recieve a more informative error message thrown from the player iframe element itself.
    this.boundHandleVimeoPlayerPostMessageEventsFunction = this.handleVimeoPlayerPostMessageEvents.bind(this);
    window.addEventListener('message', this.boundHandleVimeoPlayerPostMessageEventsFunction);

    this.vimeoPlayer.ready().then(() => {
      this.log('handleVimeoPlayerReadyEvent()::muted', this.options_.muted);
      if (this.options_.muted) this.setMuted(this.options_.muted);
      this.trigger('ready');
      this.trigger('canplay');
      if (this.playWhenReady) {
        this.playWhenReady = false;
        this.play();
      }
      this.log('this.playerStartTime:', this.playerStartTime);
      if (this.playerStartTime !== 0) {
        this.log('setting player start time...');
        this.vimeoPlayer.setCurrentTime(this.playerStartTime);
      }
    }).catch((error) => {
      console.error('Vimeo player `ready()` event error:', error);
    });

    this.vimeoPlayer.on('loaded', () => {
      this.triggerReady();
      this.trigger('ready');
      this.trigger('loadstart');
      this.trigger('loadedmetadata');
    });

    this.vimeoPlayer.on('play', () => {
      this.trigger('play');
      this._vimeoState.playing = true;
      this._vimeoState.ended = false;
    });

    this.vimeoPlayer.on('durationchange', (progress) => {
      this.log('durationchange event:', { progress });
      this.trigger('durationchange');
    });

    this.vimeoPlayer.on('timeupdate', (progress) => {
      this.log('timeupdate:', { progress });
      if (this._vimeoState.progress.duration !== progress.duration) {
        this.trigger('durationchange');
      }
      this._vimeoState.progress = progress;
      this.trigger('timeupdate');
    });

    this.vimeoPlayer.on('playbackratechange', (eventData) => {
      this._vimeoState.playbackRate = eventData.playbackRate;
      this.trigger('ratechange');
    });

    this.vimeoPlayer.on('pause', () => {
      this._vimeoState.playing = false;
      this.trigger('canplay');
      this.trigger('pause');
    });

    this.vimeoPlayer.on('ended', () => {
      this._vimeoState.playing = false;
      this._vimeoState.ended = true;
      this.trigger('ended');
    });

    this.vimeoPlayer.on('volumechange', (v) => {
      this._vimeoState.volume = v.volume;
    });

    this.vimeoPlayer.on('error', (error) => {
      // this.vimeoPlayerError = error;
      this.trigger('error', error);
    });
  }

  /**
   * Handle Vimeo Player SDK Post Message events.
   *
   * @param {Event} event
   * @returns void
   */
  handleVimeoPlayerPostMessageEvents(e) {
    if (e.origin !== VIMEO_URLS.VimeoProviderUrl) return;

    this.handleVimeoPlayerLoadedPostMessageEvent(e);
    this.handleVimeoPlayerPasswordErrorPostMessageEvent(e);
    this.handleVimeoPlayerPrivacyErrorPostMessageEvent(e);
  }

  handleVimeoPlayerLoadedPostMessageEvent(e) {
    if (
      e.data?.event === VIMEO_POST_MESSAGE_EVENTS.Loaded
      && e.data.data.id === Number(this.extractedVimeoVideoIds.sourceId)
      && this._vimeoState.passwordProtected
    ) {
      this.log('triggering password entered event...', { e });
      const detail = {
        vimeoVideoUrl: this.activeVideoId,
      };
      window.dispatchEvent(new CustomEvent('vimeo-password-entered', { detail }));
      this._vimeoState.passwordProtected = false;
      if (this.options_.service === 'Designer') {
        const [
          videoPlayerElement,
        ] = [
          document.getElementById(`video-embed-${this.options_?.bulbVideoId}`),
        ];
        videoPlayerElement?.style?.removeProperty('z-index'); // eslint-disable-line
      }
      if (this.options_.service === 'Player') {
        const [
          overlayContainerElement,
        ] = [
          document.getElementById('overlay-container'),
        ];
        overlayContainerElement.style.removeProperty('pointer-events');
      }
    }
  }

  handleVimeoPlayerPasswordErrorPostMessageEvent(e) {
    this.log('handleVimeoPlayerPasswordErrorPostMessageEvent()', { eventData: e, eventName: e?.data?.data?.name });
    if (e?.data?.data?.name !== VIMEO_POST_MESSAGE_EVENTS.PasswordError) return;
    this.log('this._vimeoState: ', { passwordProtected: this._vimeoState.passwordProtected });
    this._vimeoState.passwordProtected = true;
    this.log('this._vimeoState after: ', { passwordProtected: this._vimeoState.passwordProtected });
    if (this.options_.service === 'Designer') {
      const [
        videoPlayerElement,
      ] = [
        document.getElementById(`video-embed-${this.options_?.bulbVideoId}`),
      ];
      videoPlayerElement?.style?.setProperty('z-index', 1); // eslint-disable-line
    }
    if (this.options_.service === 'Player') {
      const [
        overlayContainerElement,
        bulbControlsWrapper,
      ] = [
        document.getElementById('overlay-container'),
        document.getElementById('controls-wrapper'),
      ];
      overlayContainerElement.style.setProperty('pointer-events', 'none');
      bulbControlsWrapper.style.setProperty('display', 'none');
      this.options_.setSnackbar({
        text: 'Please enter the password for the Vimeo video.',
        color: 'error',
        timeout: 7000,
      });
    }
  }

  handleVimeoPlayerPrivacyErrorPostMessageEvent(e) {
    if (e.data.data?.name !== VIMEO_POST_MESSAGE_EVENTS.PrivacyError) return;
    const { data: { data: vimeoReadyPromise } } = e;
    this.errorMessage = vimeoReadyPromise.message;
    this.errorName = vimeoReadyPromise.name;
    this.handleVimeoFallbackStreamLogic();
  }

  /**
   * A function that determines whether or not the Vimeo player controls are enabled.
   */
  controls() {
    return false;
  }

  /**
  * A function that determines whether or not fullscreen videos are supported.
  *
  * @return {Boolean} - true
  */
  supportsFullScreen() {
    return true;
  }

  /**
    * A function that returns the current video src.
    *
    * @return {String} this.source.src - The vimeo player's src URL.
    */
  currentSrc() {
    return this.source?.src;
  }

  /**
    * A function that returns the volume value currently stored in the child player state.
    *
    * @return {Integer} this._vimeoState.volume - The volume of the player. Ranges from 0 to 1.
    */
  volume() {
    this.log('volume()', this._vimeoState.volume);
    return this._vimeoState.volume;
  }

  /**
    * A function that sets the volume.
    *
    * @param {Integer} volume - The volume of the player. Ranges from 0 to 1.
    */
  setVolume(volume) {
    this.log('setVolume()', volume);
    return this?.vimeoPlayer?.setVolume(volume);
  }

  async handleFetchingVimeoCaptions() {
    if (!this?.vimeoPlayer) return;

    this.vimeoPlayer.getTextTracks().then((tracks) => {
      if (!tracks.length) return;

      this.vimeoPlayer.getStoredTextTracks = () => tracks;
      this.log('Vimeo video caption tracks: ', { tracks });
      const bulbHasCaptions = (() => {
        if (this.options_.service === 'Designer') {
          this.options_?.vm?.setVimeoPlayerInstance(this.vimeoPlayer); // eslint-disable-line
          return (
            !!this.options_?.vm?.segment?.captions?.integration?.length
            || !!this.options_?.vm?.segment?.captions?.playposit?.length
          );
        }
        if (this.options_.service === 'Player') {
          return this.options_?.vm?.bulb?.hasCaptions;
        }
        return false;
      })();
      this.log('Vimeo video caption tracks: ', { tracks });
      if (
        tracks.length
        && !bulbHasCaptions
      ) {
          _.map(tracks, (track) => {
            track.id = uuidv4();
            track.type = 'vimeoEmbed';
          });
          if (this.options_.service === 'Designer') {
            const captions = {
              integration: tracks,
              playposit: this.options_?.vm?.segment?.captions?.playposit,
              all() {
                return this.integration.concat(this.playposit);
              },
              hasCollectedVimeoCaptions: true,
            };
            this.options_?.vm?.updateVideoSegmentByBulbVideoId({ // eslint-disable-line
              bulbVideoId: this.options_?.vm.segment?.bulbVideoId,
              videoSegment: {
                captions,
              },
            });
          }
          if (this.options_.service === 'Player') {
            this.options_?.vm?.setInvokeVimeoFallbackCaptionLogic(true); // eslint-disable-line
            const vimeoVideoCaptionInfo = {
              vimeoVideoIds: extractVimeoVideoIdsFromUrl(
                this.activeVideoId.replace(
                  (this.activeVideoId.includes('video') ? 'https://vimeo.com/video/' : 'https://vimeo.com/'), '') // eslint-disable-line
              ),
              tracks,
            };
            this.options_?.vm?.setVimeoEmbedPlayerCaptions(vimeoVideoCaptionInfo); // eslint-disable-line
            this.options_?.vm?.setBulbHasCaptions(); // eslint-disable-line
          }
        }
    });
  }

  /**
    * A function that returns an array of caption tracks gleamed from the child player.
    *
    * @return {Array} An array of the child player's caption text tracks.
    */
  childTextTracks() {
    this.log('childTextTracks()');
    return this?._vimeoState?.textTracks;
  }

  /**
  * A function that returns the duration of the video, collected from the child player.
  *
  * @return {Integer} An integer representing the length of the child player's video in seconds.
  */
  duration() {
    this.log('duration()', this?._vimeoState?.progress?.duration);
    return this?._vimeoState?.progress?.duration;
  }

  /**
  * A function that returns whether or not the video is currently buffering.
  *
  * @return {Boolean} If `true`, then the video is buffering.
  */
  buffering() {
    this.log('buffering()');
    return this.isBuffering;
  }

  /**
  * A function that returns an array of integers that represents the buffered portions of the video.
  *
  * @return {Array} An array of integers that represents the buffered portions of the video.
  */
  buffered() {
    const progress = this?._vimeoState?.progress;
    return videojs.createTimeRange(0, progress?.percent * progress?.duration);
  }

  /**
  * A function that returns whether or not the video is currently paused.
  *
  * @return {Boolean} If `true`, then the video is paused.
  */
  paused() {
    return !this._vimeoState.playing;
  }

  /**
  * A function that pauses the Vimeo player.
  *
  */
  pause() {
    this.log('pause()');
    return this.vimeoPlayer.pause();
  }

  /**
    * A function that instructs the Vimeo player to play.
    *
    * Since the Vimeo player could sometimes be within an iframe, we can't fire off a play event until the user has
    * interacted with the page. use `initializeUserInteractionEvents()` to listen for either a click event or a scroll event,
    * either of which serves as user interaction for this purpose. Without the `setTimeout()` in the following code, the play
    * event is triggered BEFORE the user interaction click so we need `setTimeout()` here in order to ensure that the
    * user interaction click is registered before the play event.
    */
  play() {
    this.log('play()');
    let playTimeout = 0;
    // half of what the transition-delay is set to from the VideoInstance.vue component
    if (this.playerStartTime !== 0) playTimeout = 500;
    setTimeout(() => {
      this.log({
        hasLoadedVimeoPlayer: this.hasLoadedVimeoPlayer,
        hasUserInteraction,
        playing: this._vimeoState.playing,
        playTimeout,
        vimeoState: this._vimeoState,
      });
      this.log('play() 1');
      if (
        !this.hasLoadedVimeoPlayer
        || !hasUserInteraction
        || this._vimeoState.playing
      ) {
        if (!this.hasLoadedVimeoPlayer) this.playWhenReady = true;
        return;
      }
      this.log('play() 2');
      if (!this.hasTriggeredFirstPlay) {
        this.trigger('firstplay');
        this.hasTriggeredFirstPlay = true;
      }
      /* eslint no-console: "off" */
      return this.vimeoPlayer.play().catch(console.error);
    }, playTimeout);
  }

  /**
  * A getter/setter function that sets the `muted` property of the video (if an argument is provided) or returns the current `muted` property of the video (if not).
  *
  * @param {Boolean} isMuted - If `true`, then mute the video. If `false`, unmute the video.
  *
  * @return {Boolean} - Represents whether the video is currently muted or not.
  */
  muted(isMuted) {
    this.log('muted()', isMuted);
    if ([null, undefined].includes(isMuted)) return this._vimeoState.volume === 0;
    return this.setMuted(isMuted);
  }

  /**
  * A function that instructs the Vimeo player to change its volume to either 0 or 1 based on whether the video should
  * be muted or not.
  *
  * @param {Boolean} isMuted - If `true`, then mute the video. If `false`, unmute the video. If `undefined`, return the current `this._childPlayerState.muted` value.
  *
  * @return {Boolean} - Represents whether the video is currently muted or not.
  */
  setMuted(isMuted) {
    this.log('setMuted()', isMuted);
    if (isMuted) this.vimeoPlayer.setVolume(0);
    else this.vimeoPlayer.setVolume(1);
  }

  /**
    * A function that returns the error that Vimeo player has run into (if applicable.)
    *
    * @return {Object} A VideoJS error object received from the child player.
    */
  // error() {
  //   // TODO TAM:
  //   this.log('error()', this.vimeoPlayerError);
  //   return this.vimeoPlayerError;
  // }

  /**
  * A function that returns the Vimeo player's playback rate.
  *
  * @param {Integer} playbackRate - playback rate to set the video to.
  *
  * @return {Integer} - A number representing the child player's current playback rate.
  */
  playbackRate(playbackRate) {
    this.log('playbackRate()', playbackRate);
    if (typeof playbackRate !== 'number') return this.vimeoPlayer.getPlaybackRate();
    return this.setPlaybackRate(playbackRate);
  }

  /**
  * A function that changes its playback rate to the provided integer.
  *
  * @param {Integer} playbackRate - The desired playback rate for the video.
  *
  * @return {Integer} - The desired playback rate of the video.
  */
  setPlaybackRate(playbackRate) {
    this.log('setPlaybackRate()', playbackRate);
    return this.vimeoPlayer.setPlaybackRate(playbackRate);
  }

  /**
  * A getter/setter function that sets the `src` property of the video (if an argument is provided) or
  * returns the current `src` property of the video (if not).
  *
  * @param {String} src - A string representing the URL of the Proquest video.
  *
  * @return {Object} - An object containing the video source (String) as well as its type (String).
  */
  src(src) {
    if (src) this.setSrc({ src });
    return this.source;
  }

  /**
  * A function that sets the video's source before initializing the Vimeo player.
  *
  * @param {Object} source - An object containing the video source (String) as well as its type (String).
  *
  * @return {Object} - An object containing the video source (String) as well as its type (String).
  */
  setSrc(source = {}) {
    if (!source || !source?.src) return;
    delete this.errorNumber;
    this.source = source;
    this.activeVideoId = source.src;

    this.log('source && this.src():', {
      source,
      src: this.src(),
    });
    if (source.src === this.src()?.src) return;
    this.initializeVimeoPlayer();

    if (
      this.options_.autoplay
      && !_isOnMobile
    ) {
      if (this.isReady_) this.play();
      else this.playWhenReady = true;
    }
    return this.source;
  }

  /**
  * A getter/setter function that sets the `currentTime` property of the video (if an argument is provided) or returns the current `currentTime` property of the video (if not).
  *
  * @param {Integer} time - A number representing the desired time for the video to seek to.
  *
  * @return {Integer} - A number representing the desired time for the video to seek to.
  */
  currentTime(time) {
    if (
      this.isBuffering
      && parseFloat(this._vimeoState?.progress?.seconds?.toFixed(2)) === parseFloat(this.seekTime?.toFixed(2))
    ) {
      this.seekTime = null;
      this.trigger('seeked');
      this.isBuffering = false;
    }
    if (!_.isFinite(time)) return this._vimeoState.progress.seconds;
    return this.setCurrentTime(time);
  }

  /**
  * A function that seeks to the provided time.
  *
  * @param {Integer} time - The desired time for the video to seek to.
  *
  * @return {Integer} - The desired time for the video to seek to.
  */
  setCurrentTime(time) {
    if (time === this.seekTime) return;
    this.seekTime = time;
    this.isBuffering = true;
    this.trigger('seeking');
    return this.vimeoPlayer.setCurrentTime(time);
  }

  /**
  * A function that returns whether or not the child player's video is currently playing.
  *
  * @return {Boolean} - If `true`, the child player's video is playing. If `false`, it is not playing.
  */
  playing() {
    this.log('playing()', this._vimeoState.playing);
    if (this._vimeoState.playing) this.isBuffering = false;
    return this._vimeoState.playing;
  }

  /**
  * A function that returns whether or not the video has ended.
  *
  * @return {Boolean} - Represents whether the video has ended or not.
  */
  ended() {
    this.log('ended()', this._vimeoState.ended);
    return this._vimeoState.ended;
  }

  /**
    * A function that's called whenever the parent player is destroyed so that the Vimeo tech's main element can be removed from the DOM.
    *
    */
  async dispose() {
    try {
      this.log('Calling dispose from Vimeo tech...');
      if (this.vimeoPlayer) {
        // resetting state:
        this._vimeoState = {};
        this.log('disposed this._vimeoState: ', { vimeoState: this._vimeoState });
        this.el_.parentNode.className = this.el_.parentNode.className
          .replace(' vjs-vimeo', '')
          .replace(' vjs-vimeo-mobile', '');
        this.el_.parentNode.removeChild(this.el_);

        // Dispose of the Vimeo Player
        if (this.vimeoPlayer.destroy) {
          this.log('Destroying Vimeo player...');
          await this.vimeoPlayer.destroy();
          this.vimeoPlayer = null;
          this.log('Removing DOM `window message` event listener for password logic...');
          window.removeEventListener('message', this.boundHandleVimeoPlayerPostMessageEventsFunction);
        }
      }
    } catch(error) {
      console.error('Vimeo custom tech dispose error: ', { error });
    }
  }

  /**
 * Create an embed from oEmbed data inside an element.
 *
 * @param {object} data The oEmbed data.
 * @param {HTMLElement} element The element to put the iframe in.
 * @return {HTMLIFrameElement} The iframe embed.
 */
  createEmbed({ html }, element) {
    if (!element) throw new TypeError('An element must be provided');
    if (element.getAttribute('data-vimeo-initialized') !== null) return element.querySelector('iframe');

    const div = document.createElement('div');
    div.innerHTML = html;

    element.appendChild(div.firstChild);
    element.setAttribute('data-vimeo-initialized', 'true');

    return element.querySelector('iframe');
  }

  /**
 * Check to see if the URL is a Vimeo url.
 *
 * @param {string} url The url string.
 * @return {boolean} Indicating whether or not the URL is a Vimeo video URL.
 */
  isVimeoUrl(url) {
    return (/^(https?:)?\/\/((player|www)\.)?vimeo\.com(?=$|\/)/).test(url);
  }

  /**
 * Make an oEmbed call for the specified Vimeo video URL.
 *
 * @param {string} videoUrl The vimeo.com url for the video.
 * @param {Object} [params] Parameters to pass to oEmbed.
 * @param {HTMLElement} element The element.
 * @return {Promise}
 */
  getOEmbedData({
    videoUrl,
    params = {},
    element,
  } = {}) {
    return new Promise((resolve, reject) => {
      if (!this.isVimeoUrl(videoUrl)) throw new TypeError(`"${videoUrl}" is not a vimeo.com url.`);

      let url = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(videoUrl)}`;

      for (const param in params) {
        if (params.hasOwnProperty(param)) {
          url += `&${param}=${encodeURIComponent(params[param])}`;
        }
      }

      const xhr = 'XDomainRequest' in window ? new XDomainRequest() : new XMLHttpRequest(); // eslint-disable-line
      xhr.open('GET', url, true);
      xhr.onload = function xhrOnload() {
        try {
          const json = JSON.parse(xhr.responseText);

          if (xhr.status === 200 || xhr.status === 404 || xhr.status === 403) {
            // We still want to create the embed to give users visual feedback
            this.createEmbed(json, element);
            reject(new Error(`"${videoUrl}" is not embeddable.`));
            return;
          }

          // Check api response for 403 on oembed for domain level privacy
          if (json.domain_status_code === 403) {
            // We still want to create the embed to give users visual feedback
            this.createEmbed(json, element);
            reject(new Error(`"${videoUrl}" is not embeddable.`));
            return;
          }
          resolve(json);
        } catch (error) {
          reject(error);
        }
      };
      xhr.onerror = function xhrOnerror() {
        const status = xhr.status ? ` (${xhr.status})` : '';
        reject(new Error(`There was an error fetching the embed code from Vimeo${status}.`));
      };

      xhr.send();
    });
  }

  /**
 * Function to handle displaying the error to our the correct microservice.
 */
  handleVimeoFallbackStreamLogic() {
    if (this.options_.service === 'Player') {
      return this.options_?.fallbackStreamLogic();
    }
    // designer
    window.dispatchEvent(new CustomEvent('vimeoInitIssueToast', {
      detail: {
        message: this.errorMessage,
        name: this.errorName,
      },
    }));
  }
}

/**
  * A function that determines whether or not the Vimeo tech is supported.
  *
  * @return {Boolean} - true
  */
Vimeo.isSupported = function isSupported() {
  return true;
};

/**
* A function that determines whether or not the Vimeo tech supports fullscreen.
*
* @return {Boolean} - true
*/
Vimeo.supportsFullScreen = function supportsFullScreen() {
  return true;
};

/**
  * A function that determines whether or not the provided video type matches the Vimeo tech's supported video type.
  *
  * @return {Boolean} - If `true`, then the video type is supported. If `false`, then it is not.
  */
Vimeo.canPlayType = function canPlayType(type) {
  return type === 'video/vimeo';
};

/**
  * A function that determines whether or not the provided video src matches the expected video pattern AND whether or not the provided video type matches the Vimeo tech's supported video type.
  *
  * @param {String} type  - the video type.
  *
  * @return {Boolean} - If `true`, then the video src and type are supported. If `false`, then they are not.
  */
Vimeo.canPlaySource = function canPlaySource({
  type,
} = {}) {
  return Vimeo.canPlayType(type);
};

/**
 * Inject the Vimeo tech custom css.
 */
function injectCss() {
  const css = `
     .vjs-vimeo { height: 100%; width: 100%; }
     .vjs-vimeo .vjs-tech { position: absolute !important; }
     .vjs-vimeo .vjs-poster { background-size: cover; }
     .vjs-vimeo { max-width: 100%; }
     .vjs-vimeo.vjs-fullscreen .vimeo { height: 100% !important; width: 100% !important; }
   `;

  const head = document.head || document.getElementsByTagName('head')[0];
  const style = document.createElement('style');

  if (style.styleSheet) style.styleSheet.cssText = css;
  else style.appendChild(document.createTextNode(css));

  head.appendChild(style);
}

function scriptLoaded() {
  Vimeo.isScriptReady = true;
}

if (typeof document !== 'undefined') {
  loadScript('https://player.vimeo.com/api/player.js', scriptLoaded);
  injectCss();
}

registerTech('Vimeo', Vimeo);
export default Vimeo;

// For good measure: ┻━┻ ︵ヽ(`Д´)ﾉ︵  ┻━┻